import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import {
  LayoutDashboard,
  Users,
  BarChart2,
  FileDown,
  GraduationCap,
  Building2,
  Library,
  BookOpen,
  Bot,
  GitCompare,
  Server,
  Settings,
  HelpCircle,
} from "lucide-react";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";

export const FEATURE_SEARCH_DATA = [
  // Admin Tools
  { group: "Admin tools", title: "Overview", desc: "General overview of your statistics", path: "/explore/overview", icon: LayoutDashboard },
  { group: "Admin tools", title: "Assistant", desc: "AI assistant and query engine", path: "/explore/assistant", icon: Bot },
  { group: "Admin tools", title: "Collaboration", desc: "Find and analyze co-authors and networks", path: "/explore/collaborations", icon: Users },
  { group: "Admin tools", title: "Analysis", desc: "Detailed analysis of benchmarking data", path: "/explore/analysis", icon: BarChart2 },
  { group: "Admin tools", title: "Peer", desc: "Compare entity with similar peers", path: "/explore/peer", icon: GitCompare },
  { group: "Admin tools", title: "My Library", desc: "Manage generated files, export jobs, and download history", path: "/explore/my-library", icon: FileDown },

  // Explore
  { group: "Explore", title: "Faculty", desc: "Browse faculty profiles and metrics", path: "/explore/faculties", icon: GraduationCap },
  { group: "Explore", title: "Institution", desc: "Explore global institution profiles", path: "/explore/institutions", icon: Building2 },
  { group: "Explore", title: "Unit", desc: "View performance by departments and units", path: "/explore/units", icon: Library },
  { group: "Explore", title: "Venue", desc: "Search for journals and conferences", path: "/explore/venues", icon: BookOpen },

  // Developer & System
  { group: "Developer & System", title: "MCP Server", desc: "Model Context Protocol server for AI agent integrations (Coming Soon)", path: "/explore/mcp-server", icon: Server },
  { group: "Developer & System", title: "Settings", desc: "Manage account preferences, profile, and system settings", path: "/explore/settings", icon: Settings },
  { group: "Developer & System", title: "Get Help", desc: "Knowledge base, guides, and support", path: "/explore/help", icon: HelpCircle },
];

const FeatureSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const handleSelect = (path) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  // Group data for rendering
  const groupedData = FEATURE_SEARCH_DATA.reduce((acc, item) => {
    if (!acc[item.group]) {
      acc[item.group] = [];
    }
    acc[item.group].push(item);
    return acc;
  }, {});

  return (
    <div className="w-full relative">
      <Command shouldFilter={true} className="border border-input rounded-md shadow-sm overflow-visible bg-white">
        <PopoverPrimitive.Root open={open} onOpenChange={(val) => { if (!val) setOpen(false); }}>
          <PopoverPrimitive.Anchor asChild>
            <div className="w-full">
              <CommandInput
                placeholder="Search features..."
                value={query}
                onValueChange={(val) => {
                  setQuery(val);
                  setOpen(!!val);
                }}
                onFocus={() => { if (query) setOpen(true) }}
                onBlur={() => setTimeout(() => setOpen(false), 200)}
                className="h-10 text-sm border-none ring-0 focus:ring-0 shadow-none bg-gray-50 hover:bg-gray-100 transition-colors focus:bg-white placeholder:text-gray-400"
              />
            </div>
          </PopoverPrimitive.Anchor>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
              className="z-[1001] w-[var(--radix-popover-trigger-width)] rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95"
            >
              <CommandList className="max-h-[360px] overflow-y-auto">
                <CommandEmpty>No features found.</CommandEmpty>
                {Object.entries(groupedData).map(([groupName, items]) => (
                  <CommandGroup key={groupName} heading={groupName}>
                    {items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <CommandItem
                          key={item.title}
                          value={`${item.title} ${item.desc}`}
                          onSelect={() => handleSelect(item.path)}
                          className="flex items-center gap-3 py-2 cursor-pointer"
                        >
                          <Icon className="h-5 w-5 text-muted-foreground" />
                          <div className="flex flex-col">
                            <span className="font-medium text-foreground text-sm">{item.title}</span>
                            <span className="text-xs text-muted-foreground">{item.desc}</span>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      </Command>
    </div>
  );
};

export default FeatureSearch;
